import { useEffect, useState } from 'react';

/** Default poll interval: check for a new deploy every 5 minutes. */
const DEFAULT_CHECK_INTERVAL_MS = 5 * 60 * 1000;

/** Small delay before the first check so it doesn't race initial page load. */
const INITIAL_CHECK_DELAY_MS = 1000;

/**
 * Extract the app version injected into index.html by the `inject-version`
 * Vite plugin (`<script>window.__APP_VERSION__ = "...";</script>`).
 *
 * Pure and side-effect free so the parsing logic is unit-testable without a
 * DOM, timers, or fetch. Returns null when no version marker is present.
 */
export function parseInjectedVersion(html: string): string | null {
  const match = html.match(/window\.__APP_VERSION__\s*=\s*["']([^"']+)["']/);
  return match?.[1] ?? null;
}

/** The version baked into the currently running bundle, or null if unknown. */
function getRunningVersion(): string | null {
  const injected = typeof window !== 'undefined' ? window.__APP_VERSION__ : undefined;
  if (injected) return injected;
  // Fall back to the build-time define (always present in a real build).
  return typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : null;
}

export interface UseAppVersionOptions {
  /** How often to poll index.html for a new version. Default: 5 minutes. */
  checkInterval?: number;
  /** Whether polling is enabled. Default: true. */
  enabled?: boolean;
}

export interface UseAppVersionResult {
  /** True once a deployed version newer than the running one is detected. */
  updateAvailable: boolean;
  /** The running bundle's version, or null if it can't be determined. */
  currentVersion: string | null;
  /** The newly detected deployed version, or null until one is seen. */
  latestVersion: string | null;
  /** Clear caches and hard-reload to pick up the new bundle. */
  reload: () => void;
}

/**
 * Detect when a newer app bundle has been deployed than the one running in
 * this tab, so the user can be prompted to reload.
 *
 * After a rolling deploy, an open tab keeps its stale cached bundle. This hook
 * polls the (uncached) index.html, reads the version the server now injects,
 * and flags a mismatch against the running bundle's version.
 */
export function useAppVersion(options: UseAppVersionOptions = {}): UseAppVersionResult {
  const { checkInterval = DEFAULT_CHECK_INTERVAL_MS, enabled = true } = options;

  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [latestVersion, setLatestVersion] = useState<string | null>(null);

  // The running version is fixed for the tab's lifetime; capture it once via a
  // lazy initializer so it's stable and safe to read during render.
  const [currentVersion] = useState<string | null>(getRunningVersion);

  useEffect(() => {
    if (!enabled) return;
    // Without a known running version we can't compare — nothing to detect.
    if (!currentVersion) return;

    let cancelled = false;

    const checkVersion = async () => {
      try {
        // Cache-bust hard: no-store plus a query param, so we always see the
        // HTML the server injects now, not a cached copy.
        const response = await fetch(`/index.html?t=${Date.now()}`, {
          cache: 'no-store',
          headers: { 'Cache-Control': 'no-cache' },
        });
        if (!response.ok) return;

        const deployed = parseInjectedVersion(await response.text());
        if (cancelled || !deployed) return;

        if (deployed !== currentVersion) {
          setLatestVersion(deployed);
          setUpdateAvailable(true);
        }
      } catch {
        // Version checking is non-critical — stay silent on transient failures.
      }
    };

    const initialTimeout = setTimeout(checkVersion, INITIAL_CHECK_DELAY_MS);
    const interval = setInterval(checkVersion, checkInterval);

    return () => {
      cancelled = true;
      clearTimeout(initialTimeout);
      clearInterval(interval);
    };
  }, [checkInterval, enabled, currentVersion]);

  const reload = () => {
    // Best-effort cache clear, then hard reload to fetch the new bundle.
    if ('caches' in window) {
      void caches.keys().then((names) => Promise.all(names.map((name) => caches.delete(name))));
    }
    window.location.reload();
  };

  return { updateAvailable, currentVersion, latestVersion, reload };
}
